// =============================================================================
// TOKENS DE COLOR - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Tokens de color centralizados para mantener consistencia visual
// y facilitar el mantenimiento del sistema de diseño

// =============================================================================
// YOOL BRAND COLORS — espejo JS del Design System
// =============================================================================
/**
 * yoolBrand — espejo JS de los tokens del Yool Design System.
 *
 * NO ES FUENTE DE VERDAD. La fuente es
 * `apps-design-system/project/colors_and_type.css` (Claude Design 1a4bd3e4),
 * que llega a la app vía `src/styles/ds/colors_and_type.css` (`npm run ds:sync`).
 * Si un valor cambia, se cambia en el DS y se refleja aquí.
 *
 * POR QUÉ SIGUE SIENDO HEX Y NO `var(--yool-cta)`
 * -----------------------------------------------
 * Porque `withOpacity()` (más abajo) hace `parseInt(hex.substr(...), 16)`. Con
 * un `var(...)` devolvería NaN y se romperían todos los fondos con opacidad.
 * Mientras exista ese consumo, el espejo tiene que ser hex literal.
 *
 * @see docs/design-system-unico.md §4b
 */
export const yoolBrand = {
  teal:         '#007D6E',  // = --yool-cta
  tealHover:    '#006459',  // = --yool-cta-hover  (era #006358 propio, alineado al DS)
  navy:         '#0E2640',  // = --yool-navy
  black:        '#1A1A1A',  // = --yool-black
  orange:       '#D35400',  // = --fy-orange — SOLO badges, nunca CTA
  orangeSoft:   '#FFEEE8',  // = --badge-vence-bg  (era #FEF3E2 propio, alineado al DS)
  success:      '#158B5C',  // = --yool-success
  successSoft:  '#D4EEE0',  // = --yool-success-bg (era #E8F5EE propio, alineado al DS)
  error:        '#ED4068',  // = --yool-error
  errorSoft:    '#FFDDE5',  // = --yool-error-bg   (era #FDEDF1 propio, alineado al DS)
  // Neutrales v5.2 (NO slate)
  bg:           '#F7F7F7',  // = --yool-page
  surface:      '#FFFFFF',  // = --white
  surface2:     '#EBEBEB',  // = --yool-gray — hover rows
  border:       '#E0E0E0',  // = --yool-border
  textSecondary:'#54585A',  // = --yool-gray1
  textMuted:    '#8D8D8D',  // = --yool-muted

  // Extensiones de app: conceptos que el DS no define. Espejan la capa de app
  // de yool-admin.css, no el DS.
  tealSoft:     '#E6F3F1',  // = --yool-teal-soft
  navyDark:     '#0A1B2E',  // = --yool-navy-dark
};

/**
 * @deprecated Escala genérica MUI — no usar para colores de marca.
 * Para color de marca usar yoolBrand o las CSS vars (--yool-*, --fy-*).
 */
export const baseColors = {
  // Grises
  white: '#FFFFFF',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  black: '#000000',

  // Azules (Primario)
  blue50: '#EFF6FF',
  blue100: '#DBEAFE',
  blue200: '#BFDBFE',
  blue300: '#93C5FD',
  blue400: '#60A5FA',
  blue500: '#3B82F6',
  blue600: '#2563EB',
  blue700: '#1D4ED8',
  blue800: '#1E40AF',
  blue900: '#1E3A8A',

  // Verdes (Éxito)
  green50: '#F0FDF4',
  green100: '#DCFCE7',
  green200: '#BBF7D0',
  green300: '#86EFAC',
  green400: '#4ADE80',
  green500: '#22C55E',
  green600: '#16A34A',
  green700: '#15803D',
  green800: '#166534',
  green900: '#14532D',

  // Rojos (Error/Peligro)
  red50: '#FEF2F2',
  red100: '#FEE2E2',
  red200: '#FECACA',
  red300: '#FCA5A5',
  red400: '#F87171',
  red500: '#EF4444',
  red600: '#DC2626',
  red700: '#B91C1C',
  red800: '#991B1B',
  red900: '#7F1D1D',

  // Amarillos (Advertencia)
  yellow50: '#FFFBEB',
  yellow100: '#FEF3C7',
  yellow200: '#FDE68A',
  yellow300: '#FCD34D',
  yellow400: '#FBBF24',
  yellow500: '#F59E0B',
  yellow600: '#D97706',
  yellow700: '#B45309',
  yellow800: '#92400E',
  yellow900: '#78350F',

  // Índigos (Secundario)
  indigo50: '#EEF2FF',
  indigo100: '#E0E7FF',
  indigo200: '#C7D2FE',
  indigo300: '#A5B4FC',
  indigo400: '#818CF8',
  indigo500: '#6366F1',
  indigo600: '#4F46E5',
  indigo700: '#4338CA',
  indigo800: '#3730A3',
  indigo900: '#312E81',
};

/**
 * @deprecated Tokens semánticos MUI — derivados de baseColors, no de marca.
 * Para info/secondary/text migrar a CSS vars (--yool-*, --fy-cyan, --foreground).
 */
export const lightTheme = {
  // Colores primarios — Yool Verde Petróleo (Brand Book v4)
  primary: {
    50:   yoolBrand.tealSoft,
    100:  '#C7E8E4',
    200:  '#A0D5CF',
    300:  '#70BDB6',
    400:  '#3DA59C',
    500:  yoolBrand.teal,       // #007D6E — CTA principal
    600:  yoolBrand.teal,
    700:  yoolBrand.tealHover,  // #006358
    800:  '#004D45',
    900:  yoolBrand.navy,       // #0E2640
    main:  yoolBrand.teal,
    light: yoolBrand.tealSoft,
    dark:  yoolBrand.tealHover,
  },

  // Colores secundarios
  secondary: {
    50: baseColors.indigo50,
    100: baseColors.indigo100,
    200: baseColors.indigo200,
    300: baseColors.indigo300,
    400: baseColors.indigo400,
    500: baseColors.indigo500,
    600: baseColors.indigo600,
    700: baseColors.indigo700,
    800: baseColors.indigo800,
    900: baseColors.indigo900,
    main: baseColors.indigo600,
    light: baseColors.indigo100,
    dark: baseColors.indigo800,
  },

  // Estados
  success: {
    50: baseColors.green50,
    100: baseColors.green100,
    200: baseColors.green200,
    300: baseColors.green300,
    400: baseColors.green400,
    500: baseColors.green500,
    600: baseColors.green600,
    700: baseColors.green700,
    800: baseColors.green800,
    900: baseColors.green900,
    main: baseColors.green600,
    light: baseColors.green100,
    dark: baseColors.green800,
  },

  error: {
    50: baseColors.red50,
    100: baseColors.red100,
    200: baseColors.red200,
    300: baseColors.red300,
    400: baseColors.red400,
    500: baseColors.red500,
    600: baseColors.red600,
    700: baseColors.red700,
    800: baseColors.red800,
    900: baseColors.red900,
    main: baseColors.red600,
    light: baseColors.red100,
    dark: baseColors.red800,
  },

  warning: {
    50: baseColors.yellow50,
    100: baseColors.yellow100,
    200: baseColors.yellow200,
    300: baseColors.yellow300,
    400: baseColors.yellow400,
    500: baseColors.yellow500,
    600: baseColors.yellow600,
    700: baseColors.yellow700,
    800: baseColors.yellow800,
    900: baseColors.yellow900,
    main: baseColors.yellow600,
    light: baseColors.yellow100,
    dark: baseColors.yellow800,
  },

  info: {
    50: baseColors.blue50,
    100: baseColors.blue100,
    200: baseColors.blue200,
    300: baseColors.blue300,
    400: baseColors.blue400,
    500: baseColors.blue500,
    600: baseColors.blue600,
    700: baseColors.blue700,
    800: baseColors.blue800,
    900: baseColors.blue900,
    main: baseColors.blue500,
    light: baseColors.blue100,
    dark: baseColors.blue700,
  },

  // Grises
  gray: {
    50: baseColors.gray50,
    100: baseColors.gray100,
    200: baseColors.gray200,
    300: baseColors.gray300,
    400: baseColors.gray400,
    500: baseColors.gray500,
    600: baseColors.gray600,
    700: baseColors.gray700,
    800: baseColors.gray800,
    900: baseColors.gray900,
  },

  // Superficies y fondos
  background: {
    default: baseColors.white,
    paper: baseColors.white,
    level1: baseColors.gray50,
    level2: baseColors.gray100,
    level3: baseColors.gray200,
  },

  // Superficies
  surface: {
    default: baseColors.white,
    elevated: baseColors.white,
    overlay: 'rgba(0, 0, 0, 0.5)',
  },

  // Textos
  text: {
    primary: baseColors.gray900,
    secondary: baseColors.gray600,
    disabled: baseColors.gray400,
    inverse: baseColors.white,
    hint: baseColors.gray500,
  },

  // Bordes
  border: {
    default: baseColors.gray200,
    light: baseColors.gray100,
    medium: baseColors.gray300,
    strong: baseColors.gray400,
    focus: yoolBrand.teal,    // #007D6E — teal focus ring
  },

  // Divisores
  divider: baseColors.gray200,

  // Acciones
  action: {
    active: baseColors.gray600,
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 125, 110, 0.08)',   // teal #007D6E
    disabled: baseColors.gray300,
    disabledBackground: baseColors.gray100,
    focus: 'rgba(0, 125, 110, 0.12)',      // teal #007D6E
  },
};

/**
 * @deprecated Tokens semánticos MUI dark — no usar para nuevo código.
 * El dark mode usa las CSS vars dentro de .dark {} en index.css.
 */
export const darkTheme = {
  // Colores primarios — Yool Verde Petróleo (más claro en dark mode)
  primary: {
    50:   yoolBrand.navy,
    100:  '#004D45',
    200:  yoolBrand.tealHover,
    300:  yoolBrand.teal,
    400:  '#00A892',
    500:  '#00C4AB',
    600:  '#33D0BB',
    700:  '#66DCC9',
    800:  '#99E8D8',
    900:  yoolBrand.tealSoft,
    main:  '#00A892',         // teal más luminoso en dark
    light: '#33D0BB',
    dark:  yoolBrand.teal,
  },

  // Colores secundarios
  secondary: {
    50: baseColors.indigo900,
    100: baseColors.indigo800,
    200: baseColors.indigo700,
    300: baseColors.indigo600,
    400: baseColors.indigo500,
    500: baseColors.indigo400,
    600: baseColors.indigo300,
    700: baseColors.indigo200,
    800: baseColors.indigo100,
    900: baseColors.indigo50,
    main: baseColors.indigo400,
    light: baseColors.indigo300,
    dark: baseColors.indigo600,
  },

  // Estados (ajustados para tema oscuro)
  success: {
    50: baseColors.green900,
    100: baseColors.green800,
    200: baseColors.green700,
    300: baseColors.green600,
    400: baseColors.green500,
    500: baseColors.green400,
    600: baseColors.green300,
    700: baseColors.green200,
    800: baseColors.green100,
    900: baseColors.green50,
    main: baseColors.green400,
    light: baseColors.green300,
    dark: baseColors.green600,
  },

  error: {
    50: baseColors.red900,
    100: baseColors.red800,
    200: baseColors.red700,
    300: baseColors.red600,
    400: baseColors.red500,
    500: baseColors.red400,
    600: baseColors.red300,
    700: baseColors.red200,
    800: baseColors.red100,
    900: baseColors.red50,
    main: baseColors.red400,
    light: baseColors.red300,
    dark: baseColors.red600,
  },

  warning: {
    50: baseColors.yellow900,
    100: baseColors.yellow800,
    200: baseColors.yellow700,
    300: baseColors.yellow600,
    400: baseColors.yellow500,
    500: baseColors.yellow400,
    600: baseColors.yellow300,
    700: baseColors.yellow200,
    800: baseColors.yellow100,
    900: baseColors.yellow50,
    main: baseColors.yellow400,
    light: baseColors.yellow300,
    dark: baseColors.yellow600,
  },

  info: {
    50: baseColors.blue900,
    100: baseColors.blue800,
    200: baseColors.blue700,
    300: baseColors.blue600,
    400: baseColors.blue500,
    500: baseColors.blue400,
    600: baseColors.blue300,
    700: baseColors.blue200,
    800: baseColors.blue100,
    900: baseColors.blue50,
    main: baseColors.blue400,
    light: baseColors.blue300,
    dark: baseColors.blue600,
  },

  // Grises (invertidos para tema oscuro)
  gray: {
    50: baseColors.gray900,
    100: baseColors.gray800,
    200: baseColors.gray700,
    300: baseColors.gray600,
    400: baseColors.gray500,
    500: baseColors.gray400,
    600: baseColors.gray300,
    700: baseColors.gray200,
    800: baseColors.gray100,
    900: baseColors.gray50,
  },

  // Superficies y fondos
  background: {
    default: baseColors.gray900,
    paper: baseColors.gray800,
    level1: baseColors.gray800,
    level2: baseColors.gray700,
    level3: baseColors.gray600,
  },

  // Superficies
  surface: {
    default: baseColors.gray800,
    elevated: baseColors.gray700,
    overlay: 'rgba(0, 0, 0, 0.7)',
  },

  // Textos
  text: {
    primary: baseColors.gray50,
    secondary: baseColors.gray300,
    disabled: baseColors.gray500,
    inverse: baseColors.gray900,
    hint: baseColors.gray400,
  },

  // Bordes
  border: {
    default: baseColors.gray600,
    light: baseColors.gray700,
    medium: baseColors.gray500,
    strong: baseColors.gray400,
    focus: '#00A892',          // teal luminoso en dark mode
  },

  // Divisores
  divider: baseColors.gray600,

  // Acciones
  action: {
    active: baseColors.gray300,
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(0, 168, 146, 0.16)',   // teal dark luminoso
    disabled: baseColors.gray600,
    disabledBackground: baseColors.gray700,
    focus: 'rgba(0, 168, 146, 0.24)',      // teal dark luminoso
  },
};

// Colores específicos para medios de pago
export const paymentProviders = {
  mercadopago: '#00B1EA',
  flow: '#1A73E8',
  virtualpos: '#6366F1',
  webpay: '#FF6B35',
  webpaypatpass: '#FF8C42',
};

// =============================================================================
// COLORES PARA ESTADOS DE ÓRDENES DE PAGO
// =============================================================================
// Colores específicos para cada estado de orden de pago
// Mantienen consistencia visual y semántica en toda la aplicación

export const ordenPagoEstados = {
  pendiente: {
    primary: 'hsl(var(--payment-pendiente))',
    foreground: 'hsl(var(--payment-pendiente-foreground))',
    light: 'hsl(var(--payment-pendiente) / 0.1)',
    border: 'hsl(var(--payment-pendiente) / 0.5)',
    main: 'hsl(var(--payment-pendiente))',
    bg: 'hsl(var(--payment-pendiente) / 0.1)',
    dark: 'hsl(var(--payment-pendiente) / 0.8)',
  },
  procesando: {
    primary: 'hsl(var(--payment-procesando))',
    foreground: 'hsl(var(--payment-procesando-foreground))',
    light: 'hsl(var(--payment-procesando) / 0.1)',
    border: 'hsl(var(--payment-procesando) / 0.5)',
    main: 'hsl(var(--payment-procesando))',
    bg: 'hsl(var(--payment-procesando) / 0.1)',
    dark: 'hsl(var(--payment-procesando) / 0.8)',
  },
  pagado: {
    primary: 'hsl(var(--payment-pagado))',
    foreground: 'hsl(var(--payment-pagado-foreground))',
    light: 'hsl(var(--payment-pagado) / 0.1)',
    border: 'hsl(var(--payment-pagado) / 0.5)',
    main: 'hsl(var(--payment-pagado))',
    bg: 'hsl(var(--payment-pagado) / 0.1)',
    dark: 'hsl(var(--payment-pagado) / 0.8)',
  },
  expirado: {
    primary: 'hsl(var(--payment-expirado))',
    foreground: 'hsl(var(--payment-expirado-foreground))',
    light: 'hsl(var(--payment-expirado) / 0.1)',
    border: 'hsl(var(--payment-expirado) / 0.5)',
    main: 'hsl(var(--payment-expirado))',
    bg: 'hsl(var(--payment-expirado) / 0.1)',
    dark: 'hsl(var(--payment-expirado) / 0.8)',
  },
  cancelado: {
    primary: 'hsl(var(--payment-cancelado))',
    foreground: 'hsl(var(--payment-cancelado-foreground))',
    light: 'hsl(var(--payment-cancelado) / 0.1)',
    border: 'hsl(var(--payment-cancelado) / 0.5)',
    main: 'hsl(var(--payment-cancelado))',
    bg: 'hsl(var(--payment-cancelado) / 0.1)',
    dark: 'hsl(var(--payment-cancelado) / 0.8)',
  },
};

// Función helper para obtener color por estado
export const getOrdenPagoColor = (estado, variant = 'main') => {
  return ordenPagoEstados[estado]?.[variant] || ordenPagoEstados.pendiente[variant];
};

/**
 * Función para obtener las clases de Tailwind CSS para un estado de orden de pago
 * @param {string} estado - El estado de la orden (pendiente, procesando, pagado, expirado, cancelado)
 * @returns {object} Objeto con las clases de Tailwind para background, text y border
 */
export const getOrdenPagoClasses = (estado) => {
  const classMap = {
    pendiente: {
      bg: 'bg-[hsl(var(--payment-pendiente))]',
      text: 'text-[hsl(var(--payment-pendiente-foreground))]',
      border: 'border-[hsl(var(--payment-pendiente))]',
      bgLight: 'bg-[hsl(var(--payment-pendiente)/0.1)]',
      textPrimary: 'text-[hsl(var(--payment-pendiente))]'
    },
    procesando: {
      bg: 'bg-[hsl(var(--payment-procesando))]',
      text: 'text-[hsl(var(--payment-procesando-foreground))]',
      border: 'border-[hsl(var(--payment-procesando))]',
      bgLight: 'bg-[hsl(var(--payment-procesando)/0.1)]',
      textPrimary: 'text-[hsl(var(--payment-procesando))]'
    },
    pagado: {
      bg: 'bg-[hsl(var(--payment-pagado))]',
      text: 'text-[hsl(var(--payment-pagado-foreground))]',
      border: 'border-[hsl(var(--payment-pagado))]',
      bgLight: 'bg-[hsl(var(--payment-pagado)/0.1)]',
      textPrimary: 'text-[hsl(var(--payment-pagado))]'
    },
    expirado: {
      bg: 'bg-[hsl(var(--payment-expirado))]',
      text: 'text-[hsl(var(--payment-expirado-foreground))]',
      border: 'border-[hsl(var(--payment-expirado))]',
      bgLight: 'bg-[hsl(var(--payment-expirado)/0.1)]',
      textPrimary: 'text-[hsl(var(--payment-expirado))]'
    },
    cancelado: {
      bg: 'bg-[hsl(var(--payment-cancelado))]',
      text: 'text-[hsl(var(--payment-cancelado-foreground))]',
      border: 'border-[hsl(var(--payment-cancelado))]',
      bgLight: 'bg-[hsl(var(--payment-cancelado)/0.1)]',
      textPrimary: 'text-[hsl(var(--payment-cancelado))]'
    }
  };
  
  return classMap[estado] || classMap.pendiente;
};

/**
 * Función para obtener valores de color reales para uso en JavaScript (charts, estilos inline, etc.)
 * @param {string} estado - El estado de la orden (pendiente, procesando, pagado, expirado, cancelado)
 * @param {string} variant - La variante del color (primary, bg, border, etc.)
 * @returns {string} Valor de color real en formato hex o hsl
 */
export const getOrdenPagoColorValue = (estado, variant = 'primary') => {
  // Mapeo de colores reales basados en los temas de Shadcn
  const colorMap = {
    pendiente: {
      primary: 'oklch(0.705 0.213 47.604)', // naranja - orange theme
      bg: 'oklch(0.705 0.213 47.604 / 0.1)',
      border: 'oklch(0.705 0.213 47.604 / 0.5)',
      dark: 'oklch(0.646 0.222 41.116)',
      light: 'oklch(0.765 0.204 53.092)',
      main: 'oklch(0.705 0.213 47.604)'
    },
    procesando: {
      primary: 'oklch(0.623 0.214 259.815)', // azul - blue theme
      bg: 'oklch(0.623 0.214 259.815 / 0.1)',
      border: 'oklch(0.623 0.214 259.815 / 0.5)',
      dark: 'oklch(0.546 0.245 262.881)',
      light: 'oklch(0.700 0.183 256.749)',
      main: 'oklch(0.623 0.214 259.815)'
    },
    pagado: {
      primary: 'oklch(0.723 0.219 149.579)', // verde - green theme
      bg: 'oklch(0.723 0.219 149.579 / 0.1)',
      border: 'oklch(0.723 0.219 149.579 / 0.5)',
      dark: 'oklch(0.696 0.17 162.48)',
      light: 'oklch(0.750 0.268 136.678)',
      main: 'oklch(0.723 0.219 149.579)'
    },
    expirado: {
      primary: 'oklch(0.637 0.237 25.331)', // rojo - red theme
      bg: 'oklch(0.637 0.237 25.331 / 0.1)',
      border: 'oklch(0.637 0.237 25.331 / 0.5)',
      dark: 'oklch(0.577 0.245 27.325)',
      light: 'oklch(0.697 0.229 23.337)',
      main: 'oklch(0.637 0.237 25.331)'
    },
    cancelado: {
      primary: 'oklch(0.556 0 0)', // gris - default theme neutral
      bg: 'oklch(0.556 0 0 / 0.1)',
      border: 'oklch(0.556 0 0 / 0.5)',
      dark: 'oklch(0.456 0 0)',
      light: 'oklch(0.656 0 0)',
      main: 'oklch(0.556 0 0)'
    }
  };
  
  return colorMap[estado]?.[variant] || colorMap.pendiente[variant];
};

// Colores para estados de pagos - usando OKLCH para consistencia con temas shadcn/ui
export const paymentStatusColors = {
  pending: {
    background: 'oklch(0.795 0.184 86.047)', // yellow theme primary
    border: 'oklch(0.795 0.184 86.047)',
    text: 'oklch(0.421 0.095 57.708)'        // yellow theme primary-foreground
  },
  active: {
    background: 'oklch(0.723 0.219 149.579)', // green theme primary
    border: 'oklch(0.723 0.219 149.579)',
    text: 'oklch(0.982 0.018 155.826)'        // green theme primary-foreground
  },
  AUTHORIZED: {
    background: 'oklch(0.723 0.219 149.579)', // green theme primary
    border: 'oklch(0.723 0.219 149.579)',
    text: 'oklch(0.982 0.018 155.826)'        // green theme primary-foreground
  },
  processing: {
    background: 'oklch(0.623 0.214 259.815)', // blue theme primary
    border: 'oklch(0.623 0.214 259.815)',
    text: 'oklch(0.97 0.014 254.604)'         // blue theme primary-foreground
  },
  completed: {
    background: 'oklch(0.723 0.219 149.579)', // green theme primary
    border: 'oklch(0.723 0.219 149.579)',
    text: 'oklch(0.982 0.018 155.826)'        // green theme primary-foreground
  },
  paid: {
    background: 'oklch(0.723 0.219 149.579)', // green theme primary
    border: 'oklch(0.723 0.219 149.579)',
    text: 'oklch(0.982 0.018 155.826)'        // green theme primary-foreground
  },
  failed: {
    background: 'oklch(0.637 0.237 25.331)',  // red theme primary
    border: 'oklch(0.637 0.237 25.331)',
    text: 'oklch(0.971 0.013 17.38)'          // red theme primary-foreground
  },
  cancelled: {
    background: 'oklch(0.556 0 0)',           // neutral gray
    border: 'oklch(0.556 0 0)',
    text: 'oklch(0.985 0 0)'                  // white
  },
  refunded: {
    background: 'oklch(0.645 0.246 16.439)',  // rose theme primary
    border: 'oklch(0.645 0.246 16.439)',
    text: 'oklch(0.969 0.015 12.422)'         // rose theme primary-foreground
  },
  default: {
    background: 'oklch(0.97 0 0)',            // light gray background
    border: 'oklch(0.922 0 0)',               // light gray border
    text: 'oklch(0.556 0 0)'                  // medium gray text
  }
};

export const getPaymentStatusColorValue = (status) => {
  const colorConfig = paymentStatusColors[status] || paymentStatusColors.default;
  return {
    background: `hsl(${colorConfig.background})`,
    border: `hsl(${colorConfig.border})`,
    text: `hsl(${colorConfig.text})`
  };
};

// Exportar tema por defecto
export const colors = lightTheme;

// Utilidad para obtener color con opacidad
export const withOpacity = (color, opacity) => {
  // Si el color ya tiene alpha, reemplazarlo
  if (color.includes('rgba')) {
    return color.replace(/[\d\.]+\)$/g, `${opacity})`);
  }
  
  // Si es hex, convertir a rgba
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  
  // Si es rgb, convertir a rgba
  if (color.includes('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }
  
  return color;
};

// Utilidad para obtener contraste
export const getContrastColor = (backgroundColor, theme = lightTheme) => {
  // Lógica simple: si el fondo es oscuro, usar texto claro
  const darkColors = [theme.gray[700], theme.gray[800], theme.gray[900]];
  const isLight = !darkColors.includes(backgroundColor);
  
  return isLight ? theme.text.primary : theme.text.inverse;
};

/**
 * Obtiene el color para un estado de contratante
 * @param {string} estado - El estado del contratante
 * @returns {Object} Objeto con colores para el estado
 */
export const getColorByState = (estado) => {
  const stateColors = {
    pendiente: {
      background: lightTheme.warning.light,
      border: lightTheme.warning.main,
      text: lightTheme.warning.dark,
      main: lightTheme.warning.main
    },
    pendiente_dps: {
      background: lightTheme.info.light,
      border: lightTheme.info.main,
      text: lightTheme.info.dark,
      main: lightTheme.info.main
    },
    activo: {
      background: lightTheme.success.light,
      border: lightTheme.success.main,
      text: lightTheme.success.dark,
      main: lightTheme.success.main
    },
    pendiente_pago: {
      background: lightTheme.error.light,
      border: lightTheme.error.main,
      text: lightTheme.error.dark,
      main: lightTheme.error.main
    },
    inactivo: {
      background: lightTheme.gray[100],
      border: lightTheme.gray[400],
      text: lightTheme.gray[600],
      main: lightTheme.gray[500]
    }
  };

  return stateColors[estado] || stateColors.inactivo;
};

export const getClassByState = (estado) => {
  const stateClasses = {
    pendiente: {
      background: 'bg-yellow-100',
      border: 'border-yellow-500',
      text: 'text-yellow-800',
      badge: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    },
    pendiente_dps: {
      background: 'bg-blue-100',
      border: 'border-blue-500',
      text: 'text-blue-800',
      badge: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    activo: {
      background: 'bg-green-100',
      border: 'border-green-500',
      text: 'text-green-800',
      badge: 'bg-green-100 text-green-800 border-green-200'
    },
    pendiente_pago: {
      background: 'bg-red-100',
      border: 'border-red-500',
      text: 'text-red-800',
      badge: 'bg-red-100 text-red-800 border-red-200'
    },
    inactivo: {
      background: 'bg-gray-100',
      border: 'border-gray-400',
      text: 'text-gray-600',
      badge: 'bg-gray-100 text-gray-600 border-gray-200'
    }
  };

  return stateClasses[estado] || stateClasses.inactivo;
};

// Exportar todo
export default {
  base: baseColors,
  light: lightTheme,
  dark: darkTheme,
  colors: lightTheme,
  withOpacity,
  getContrastColor,
  getColorByState,
  getClassByState,
};